import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Volume2, VolumeX, Hand } from 'lucide-react';
import axios from 'axios';
import { ParkWiseLogo } from '../../components/ui/parkwise-logo';
import { getStoredKioskId } from './KioskSetup';

const API = '/api';

// Mapping from payment kiosk identity to signage screen identity
// This mapping is for attract mode / signage ONLY - does not affect payment routing
const SIGNAGE_MAPPING = {
  "kiosk_001": {  // Foot of Ramp - Entry Terminal
    screen_id: "screen_9b2b4dfa",
    site_id: "site_1d138cba"
  },
  "kiosk_002": {  // Undercroft - Exit Terminal
    screen_id: "screen_5d2b0c17",
    site_id: "site_1d138cba"
  }
};

// Fallback for unknown kiosks
const DEFAULT_SIGNAGE = {
  screen_id: "kiosk_screen_001",
  site_id: "site_kyle_001"
};

// Default configuration
const DEFAULT_CONFIG = {
  idleTimeoutSeconds: 60,
  advertRotationSeconds: 10,
  touchToStartEnabled: true,
  showLogo: true,
  showClock: true,
  emergencyPriority: true
};

/**
 * Kiosk Attract Mode / Idle Screen
 * Optimized for 65-inch portrait touchscreen
 * 
 * Signage mapping is separate from payment kiosk routing:
 * - kiosk_001 → screen_9b2b4dfa (Foot of Ramp)
 * - kiosk_002 → screen_5d2b0c17 (Undercroft)
 */
export default function KioskAttract() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [adverts, setAdverts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEmergency, setIsEmergency] = useState(false);
  const [emergencyMessage, setEmergencyMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);  // Attract audio unmuted by default
  const [pulseVisible, setPulseVisible] = useState(true);
  const videoRef = useRef(null);
  const rotationTimerRef = useRef(null);
  const [currentContentId, setCurrentContentId] = useState(null);  // Track content for video reload

  // Get signage identity from stored kiosk identity
  const kioskId = getStoredKioskId() || "kiosk_001";
  const signage = SIGNAGE_MAPPING[kioskId] || DEFAULT_SIGNAGE;
  const { screen_id: SCREEN_ID, site_id: SITE_ID } = signage;

  // Fetch adverts from playback API
  const fetchAdverts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/playback/screen/${SCREEN_ID}?site_id=${SITE_ID}`);
      const data = response.data;
      
      if (data.is_emergency && data.emergency_message) {
        setIsEmergency(true);
        setEmergencyMessage(data.emergency_message);
      } else {
        setIsEmergency(false);
        setEmergencyMessage('');
      }
      
      const mediaItems = (data.items || []).filter(item => 
        item.content_type === 'image' || item.content_type === 'video'
      );
      
      if (mediaItems.length > 0) {
        setAdverts(mediaItems);
      } else {
        setAdverts([{
          content_id: 'default_branding',
          content_type: 'image',
          name: 'ParkWise Welcome',
          url: '/parkwise-logo.png',
          duration_seconds: 10
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch adverts:', error);
      setAdverts([{
        content_id: 'fallback',
        content_type: 'image',
        name: 'ParkWise',
        url: '/parkwise-logo.png',
        duration_seconds: 10
      }]);
    } finally {
      setLoading(false);
    }
  }, [SCREEN_ID, SITE_ID]);

  // Log impression when advert changes
  const logImpression = useCallback(async (contentId, contentName) => {
    try {
      await axios.post(`${API}/playback/${SCREEN_ID}/impression`, {
        content_id: contentId,
        content_name: contentName,
        site_id: SITE_ID,
        screen_id: SCREEN_ID,
        kiosk_id: kioskId,
        context: 'attract_mode'
      });
    } catch (error) {
      console.warn('Impression logging failed:', error);
    }
  }, [SCREEN_ID, SITE_ID, kioskId]);

  // Initial load
  useEffect(() => {
    fetchAdverts();
    const refreshInterval = setInterval(fetchAdverts, 5 * 60 * 1000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(clockInterval);
    };
  }, [fetchAdverts]);

  // Advert rotation
  useEffect(() => {
    if (adverts.length <= 1 || isEmergency) return;
    
    const currentAd = adverts[currentIndex];
    const duration = (currentAd?.duration_seconds || config.advertRotationSeconds) * 1000;
    
    rotationTimerRef.current = setTimeout(() => {
      const nextIndex = (currentIndex + 1) % adverts.length;
      setCurrentIndex(nextIndex);
      logImpression(adverts[nextIndex]?.content_id, adverts[nextIndex]?.name);
    }, duration);
    
    return () => {
      if (rotationTimerRef.current) {
        clearTimeout(rotationTimerRef.current);
      }
    };
  }, [currentIndex, adverts, config.advertRotationSeconds, isEmergency, logImpression]);

  // Log initial impression
  useEffect(() => {
    if (adverts.length > 0 && adverts[0]?.content_id) {
      logImpression(adverts[0].content_id, adverts[0].name);
    }
  }, [adverts, logImpression]);

  // P0 FIX: Force video reload when content/playlist changes
  // HTML5 video elements don't automatically reload when src changes
  // We must call .load() to apply the new source, or use a key to force remount
  useEffect(() => {
    const currentAd = adverts[currentIndex];
    const newContentId = currentAd?.content_id;
    
    // Detect content change (playlist change or rotation to different content)
    if (newContentId && newContentId !== currentContentId) {
      setCurrentContentId(newContentId);
      
      // If video element exists and this is video content, force reload
      if (videoRef.current && currentAd?.content_type === 'video') {
        console.log(`[KioskAttract] Content changed: ${currentContentId} → ${newContentId}, reloading video`);
        videoRef.current.load();
        videoRef.current.play().catch(err => {
          console.warn('[KioskAttract] Video autoplay failed:', err);
        });
      }
    }
  }, [adverts, currentIndex, currentContentId]);

  // Pulse animation for touch prompt
  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setPulseVisible(prev => !prev);
    }, 1500);
    return () => clearInterval(pulseInterval);
  }, []);

  // Handle touch/click to navigate to home
  const handleTouch = () => {
    navigate('/kiosk');
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const currentAd = adverts[currentIndex];

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#1e3a5f' }}
        onClick={handleTouch}
      >
        <div className="text-center">
          <ParkWiseLogo size="kiosk" className="mb-8" />
          <div className="w-20 h-20 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Emergency override display
  if (isEmergency && emergencyMessage) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center bg-red-600 p-12 cursor-pointer"
        onClick={handleTouch}
        data-testid="kiosk-emergency-override"
      >
        <AlertTriangle className="w-32 h-32 text-white mb-12 animate-pulse" />
        <h1 className="text-5xl md:text-7xl font-bold text-white text-center mb-8">
          Important Notice
        </h1>
        <p className="text-3xl md:text-4xl text-white/90 text-center max-w-5xl leading-relaxed">
          {emergencyMessage}
        </p>
        <div className="mt-16 flex items-center gap-4 text-white/70">
          <Hand className="w-10 h-10" />
          <span className="text-2xl">Touch screen to continue</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative overflow-hidden cursor-pointer"
      style={{ backgroundColor: '#1e3a5f' }}
      onClick={handleTouch}
      data-testid="kiosk-attract-mode"
    >
      {/* Full-screen advert content */}
      <div className="absolute inset-0">
        {currentAd?.content_type === 'video' ? (
          <video
            key={currentAd.content_id}  /* P0 FIX: Force remount on content change */
            ref={videoRef}
            src={currentAd.media_url || currentAd.url}
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted={isMuted}
            playsInline
            data-testid="attract-video"
          />
        ) : (
          <div 
            className="w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: (currentAd?.media_url || currentAd?.url) ? `url(${currentAd.media_url || currentAd.url})` : 'none',
              backgroundColor: '#1e3a5f'
            }}
            data-testid="attract-image"
          >
            {/* Fallback branding content */}
            {(!(currentAd?.media_url || currentAd?.url) || currentAd.content_id === 'fallback' || currentAd.content_id === 'default_branding') && (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <ParkWiseLogo size="kiosk" />
                <p 
                  className="mt-8 text-3xl text-white/70"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Designated Space Management
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/50" />

      {/* Permanent Top Banner - TOUCH THE SCREEN TO START */}
      <div 
        className="absolute top-0 left-0 right-0 py-8 text-center z-20"
        style={{ backgroundColor: 'rgba(30, 58, 95, 0.95)' }}
      >
        <p 
          className={`text-white text-4xl md:text-5xl font-bold tracking-widest transition-opacity duration-500 ${pulseVisible ? 'opacity-100' : 'opacity-70'}`}
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          TOUCH THE SCREEN TO START
        </p>
      </div>

      {/* Header with logo and time */}
      <header className="absolute top-32 left-0 right-0 px-8 flex items-center justify-between z-10">
        <ParkWiseLogo size="kiosk" />
        
        <div className="text-right">
          <p 
            className="text-6xl font-bold text-white"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {formatTime(currentTime)}
          </p>
          <p className="text-xl text-white/70 mt-2">
            {formatDate(currentTime)}
          </p>
        </div>
      </header>

      {/* Touch to Start prompt - Bottom */}
      <div className="absolute bottom-0 left-0 right-0 pb-16 flex flex-col items-center justify-center z-10">
        <div 
          className={`flex items-center gap-6 mb-6 transition-transform duration-1000 ${pulseVisible ? 'scale-110' : 'scale-100'}`}
        >
          <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Hand className="w-12 h-12 text-white" />
          </div>
        </div>
        <p 
          className="text-4xl font-bold text-white text-center"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Touch anywhere to begin
        </p>
        <p className="text-2xl text-white/60 mt-4">
          Register &bull; Pay Before Returning to Vehicle &bull; Check Status
        </p>
      </div>

      {/* Advert indicators (dots) */}
      {adverts.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
          {adverts.map((_, idx) => (
            <div
              key={idx}
              className={`h-3 rounded-full transition-all duration-300 ${
                idx === currentIndex ? 'bg-white w-10' : 'bg-white/40 w-3'
              }`}
            />
          ))}
        </div>
      )}

      {/* Video mute toggle (if video content) */}
      {currentAd?.content_type === 'video' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMuted(!isMuted);
          }}
          className="absolute bottom-8 right-8 w-16 h-16 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors z-20"
          data-testid="mute-toggle"
        >
          {isMuted ? <VolumeX className="w-8 h-8" /> : <Volume2 className="w-8 h-8" />}
        </button>
      )}
    </div>
  );
}
